import { useState, useEffect, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { cloneDeep } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

// Hooks
import useDataSourceManager from 'hooks/useDataSourceManager';

// Components
import { OutlineButton } from 'components/buttons/OutlineButton';
import { SolidButton } from 'components/buttons/SolidButton';

import CollapseCard from 'components/Collapse';
import GlobalAlert from 'components/Notification';

import SignalQualityBarChart from './components/SignalQualityBarChart';
import WeakClientTable from './components/WeakClientTable';
// import AnalysisTable from './components/AnalysisTable';

import icn_linebar_chart from 'assets/images/icons/icn_linebar_chart.svg';

const checkMockup = process.env.REACT_APP_IS_MOCKUP && process.env.REACT_APP_IS_MOCKUP === 'true' ? true : false;

// const defaultAnalysisInfo = {
//   confidence: '-',
//   impact: '-',
//   meanRssi: '-',
//   weak: '-',
// };

const WirelessCoverageDrawer = ({ setDrawerOpen, auth, standalone, wirelessCoverageInfo, reloadPage }) => {
  const { dataMap, getData, sendRequest, sendBatch } = useDataSourceManager();
  const showEditGeneral = useMemo(() => auth.permissions !== 'Viewer', [auth]);

  const [devicesUuid, _setDevicesUuid] = useState(uuidv4());
  const [updateThresholdUuid, _setUpdateThresholdUuid] = useState(uuidv4());

  const [weakClientList, setWeakClientList] = useState([]);
  // const [analysisInfo, setAnalysisInfo] = useState(cloneDeep(defaultAnalysisInfo));
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [hasChanged, setHasChanged] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [alertInfo, setAlertInfo] = useState({
    open: false,
    severity: '',
    message: '',
  });

  const handleClickApplyBtn = () => {
    const resourcePath = `/orgs/:orgId/sites/:siteId/wirelessCoverage/signalQualification/threshold.update`;

    const restfulParamsConfig = {
      standalone: {
        orgId: auth.realm?.id,
        siteId: auth.sites?.id,
      },
      nonStandalone: {
        orgId: auth.realm?.id,
        siteId: auth.sites?.id,
      },
    };

    const mode = standalone ? 'standalone' : 'nonStandalone';
    const restfulParams = restfulParamsConfig[mode];

    const thresholdValue = selectedIndex || 0;

    const payload = {
      data: {
        threshold: thresholdValue,
      },
    };

    sendRequest(resourcePath, updateThresholdUuid, restfulParams, payload);
  };

  const fetchDevices = () => {
    const apiUrl = `/orgs/:orgId/sites/:siteId/wirelessCoverage/signalQualification/devices.read`;

    const restfulParamsConfig = {
      standalone: {
        orgId: auth.realm?.id,
        siteId: auth.sites?.id,
      },
      nonStandalone: {
        orgId: auth.realm?.id,
        siteId: auth.sites?.id,
      },
    };

    const mode = standalone ? 'standalone' : 'nonStandalone';
    const restfulParams = restfulParamsConfig[mode];

    return {
      apiKey: apiUrl,
      restfulPlaceholders: restfulParams,
    };
  };

  // const parseAnalysisInfo = () => {
  //   const targetWeakPercent = wirelessCoverageInfo?.originData?.weakPercent;
  //   let impactLevelLabel = null;
  //   let confidenceLabel = null;
  //   if (targetWeakPercent < 5) {
  //     // Weak < 5%
  //     impactLevelLabel = 'Low';
  //     confidenceLabel = 'Good';
  //   } else if (targetWeakPercent >= 5 && targetWeakPercent <= 15) {
  //     // Weak 5% ~ 15%
  //     impactLevelLabel = 'Moderate';
  //     confidenceLabel = 'WARN';
  //   } else if (targetWeakPercent > 15) {
  //     // Weak > 15%
  //     impactLevelLabel = 'High';
  //     confidenceLabel = 'BAD';
  //   } else {
  //     // others
  //     impactLevelLabel = '-';
  //     confidenceLabel = '-';
  //   }

  //   setAnalysisInfo({
  //     ...analysisInfo,
  //     confidence: confidenceLabel,
  //     impact: impactLevelLabel,
  //     meanRssi: wirelessCoverageInfo?.meanRSSI || '-',
  //     weak: wirelessCoverageInfo?.weakPercent || '-',
  //   });
  // };

  const init = async () => {
    setIsLoading(true);

    const devicesParams = fetchDevices();

    const { getSendBatchData } = await sendBatch([{ ...devicesParams, requestUuid: devicesUuid }]);

    const weakClientRes = getSendBatchData({ requestUuid: devicesUuid })?.data;

    if (weakClientRes) {
      if (checkMockup) {
        setIsLoading(false);
        setWeakClientList(weakClientRes?.devices);
        // parseAnalysisInfo(weakClientRes?.devices);
      } else {
        if (weakClientRes.hasOwnProperty('code') && weakClientRes?.code !== 200) {
          setAlertInfo({
            open: true,
            severity: 'error',
            message: weakClientRes?.message,
          });
          setWeakClientList([]);
          // setAnalysisInfo(cloneDeep(defaultAnalysisInfo));
        } else {
          setIsLoading(false);
          setWeakClientList(weakClientRes?.devices);
          // parseAnalysisInfo(weakClientRes?.devices);
        }
      }
    } else {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const isUpdateThresholdResponse = getData(updateThresholdUuid);

    if (!isUpdateThresholdResponse) return;

    if (checkMockup) {
      if (isUpdateThresholdResponse && isUpdateThresholdResponse?.data) {
        reloadPage(isUpdateThresholdResponse.data);
        setDrawerOpen(false);
      }
    } else {
      if (isUpdateThresholdResponse) {
        if (isUpdateThresholdResponse.hasOwnProperty('code') && isUpdateThresholdResponse?.code !== 200) {
          setAlertInfo({
            open: true,
            severity: 'error',
            message: isUpdateThresholdResponse?.message,
          });
        } else {
          reloadPage();
          setDrawerOpen(false);
        }
      }
    }
  }, [dataMap]);

  useEffect(() => {
    const originThreshold = wirelessCoverageInfo?.originData?.threshold;
    if (typeof originThreshold === 'number' && typeof selectedIndex === 'number') {
      setHasChanged(originThreshold !== selectedIndex);
    }
  }, [selectedIndex]);

  useEffect(() => {
    init();
  }, []);

  return (
    <Box sx={{ minHeight: '120vh', p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box component="img" src={icn_linebar_chart} sx={{ width: '20px', height: '20px', mr: 1.5 }} />
        <Typography sx={{ color: '#172664', fontSize: 15 }}>Wireless Signal Quality</Typography>
      </Box>
      <CollapseCard title="Signal Qualification">
        <SignalQualityBarChart
          selectedIndex={selectedIndex}
          setSelectedIndex={setSelectedIndex}
          barChartData={wirelessCoverageInfo?.series}
          targetThreshold={wirelessCoverageInfo?.originData?.threshold}
        />
      </CollapseCard>
      <CollapseCard title={`Weak Client (<${wirelessCoverageInfo?.originData?.threshold} dBm)`}>
        <WeakClientTable isLoading={isLoading} weakClientList={weakClientList} />
      </CollapseCard>
      {/* <CollapseCard title="Analysis">
        <AnalysisTable analysisInfo={analysisInfo} targetThreshold={wirelessCoverageInfo?.originData?.threshold} />
      </CollapseCard> */}

      {hasChanged && (
        <Box
          sx={{
            position: 'fixed',
            bottom: '15px',
            right: '30px',
            zIndex: 1000,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'primary.lightBlue',
              width: '530px',
              height: '64px',
              gap: 2,
            }}
          >
            <OutlineButton
              width="100px"
              height="32px"
              onClick={() => {
                const originThreshold = wirelessCoverageInfo?.originData?.threshold;
                setSelectedIndex(originThreshold); // Reset
              }}
            >
              Cancel
            </OutlineButton>
            <SolidButton width="100px" height="32px" onClick={handleClickApplyBtn} disabled={!showEditGeneral}>
              Apply
            </SolidButton>
          </Box>
        </Box>
      )}

      <GlobalAlert open={alertInfo.open} severity={alertInfo.severity} message={alertInfo.message} onClose={() => setAlertInfo(prev => ({ ...prev, open: false }))} />
    </Box>
  );
};

export default WirelessCoverageDrawer;
